import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useDynamicTableHeight } from "@/hooks/useDynamicTableHeight";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
}

const DataTableFolders = <TData, TValue>({
  columns,
  data,
}: DataTableProps<TData, TValue>) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const { pageSize, tableHeight, containerRef } = useDynamicTableHeight({
    estimatedRowHeight: 52,
    minRows: 5,
    maxRows: 50,
  });

  const tableFolders = useReactTable({
    data: Array.isArray(data) ? data : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: pageSize,
      },
    },
  });

  // Update page size when it changes
  useEffect(() => {
    tableFolders.setPageSize(pageSize);
  }, [pageSize, tableFolders]);

  return (
    <div ref={containerRef} className="flex flex-col h-full">
      <div className="flex items-center py-4 justify-between">
        <Input
          placeholder="Buscar por carpeta..."
          value={tableFolders.getColumn("carpeta")?.getFilterValue() as string}
          onChange={(event) =>
            tableFolders
              .getColumn("carpeta")
              ?.setFilterValue(event.target.value)
          }
          className="max-w-md w-80"
        />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div
          className="flex-1 overflow-y-auto"
          style={{ height: `${Math.max(300, tableHeight)}px` }}
        >
          <Table>
            <TableHeader>
              {tableFolders.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        className={
                          header.id === "actions"
                            ? "w-[50px] max-w-[50px] truncate"
                            : undefined
                        }
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {tableFolders.getRowModel().rows?.length ? (
                tableFolders.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="truncate">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No hay resultados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between space-x-2 py-4 w-full border-t">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>
              {tableFolders.getFilteredRowModel().rows.length} carpetas
            </span>
            <span>•</span>
            <span>
              Página {tableFolders.getState().pagination.pageIndex + 1} de{" "}
              {tableFolders.getPageCount()}
            </span>
            <span>•</span>
            <span>{pageSize} elementos por página</span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant={"outline"}
              className="border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableFolders.previousPage()}
              disabled={!tableFolders.getCanPreviousPage()}
            >
              Anterior
            </Button>
            <Button
              variant={"outline"}
              className="border-gray-200 hover:bg-gray-100 text-black shadow-sm"
              size="sm"
              onClick={() => tableFolders.nextPage()}
              disabled={!tableFolders.getCanNextPage()}
            >
              Siguiente
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataTableFolders;
